import React from "react";
import { useForm } from 'react-hook-form';
import RFG from "../../libs/inputs/RFG";
import { Box, Typography, Grid, Button } from '@mui/material';
import XDateRange from "../extra/XDateRange";
import dayjs from "dayjs";

export default function Form({
  title,
  companies = [],
  projects = [],
  cameras = [],
  defaultValues = {},
  onSubmit = (data) => { },
  onChange = (column, data) => { },
  back = () => { }
}) {
  const [selectedCam, setSelectedCam] = React.useState(null);
  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: defaultValues,
    mode: "all"
  });

  const companyVal = watch("company");
  const projectVal = watch("project");
  const cameraId = watch("camera");

  const prevCompanyRef = React.useRef();
  const prevProjectRef = React.useRef();

  // Trigger onChange when company or project changes
  React.useEffect(() => {
    if (companyVal && companyVal !== prevCompanyRef.current) {
      prevCompanyRef.current = companyVal;
      onChange("company", companyVal);
    }
  }, [companyVal, onChange]);

  React.useEffect(() => {
    if (projectVal && projectVal !== prevProjectRef.current) {
      prevProjectRef.current = projectVal;
      onChange("project", projectVal);
    }
  }, [projectVal, onChange]);

  React.useEffect(() => {
    if (watch("day_only") === undefined) {
      setValue("day_only", defaultValues?.day_only !== undefined ? defaultValues.day_only : true);
    }
  }, [defaultValues, setValue, watch]);

  React.useEffect(() => {
    if (!cameraId) {
      setSelectedCam(null);
      return;
    }
    const found = (cameras || []).find((camera) => String(camera?.id) === String(cameraId)) || null;
    setSelectedCam(prev => (prev?.id === found?.id ? prev : found));
  }, [cameraId, cameras]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ p: 3 }}>
        {title && (
          <Typography variant="h5" align="center" gutterBottom>
            {title}
          </Typography>
        )}

        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <RFG name="company" label="Company" control={control} rules={{ required: true }} className="mb-2">
              <select>
                <option value="">Select Company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.company_name}
                  </option>
                ))}
              </select>
            </RFG>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>

            <RFG name="project" label="Project Name" control={control} rules={{ required: true }} className="mb-2">
              <select>
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.project_name}
                  </option>
                ))}
              </select>
            </RFG>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>

            <RFG name="camera" label="Camera Name" control={control} rules={{ required: true }} className="mb-2">
              <select>
                <option value="">Select Camera</option>
                {cameras.map((camera) => (
                  <option key={camera.id} value={camera.id}>
                    {camera.camera_name}
                  </option>
                ))}
              </select>
            </RFG>
            {selectedCam?.id && (
              <Typography sx={{ fontSize: 12, textAlign: 'left' }}>
                Images available from:<br />
                {selectedCam?.first_image_time 
                  ? `${dayjs(selectedCam.first_image_time).format('DD-MMM-YYYY hh:mm:ss')} to ${dayjs(selectedCam.latest_image_time).format('DD-MMM-YYYY hh:mm:ss')}` 
                  : "No image dates found"}
              </Typography>
            )}
          </Grid>

          {/* Right Column */}
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>

            <RFG
              name="name"
              label="Name"
              control={control}
              rules={{
                required: true, maxLength: 500, pattern: {
                  value: /^[A-Za-z0-9\s\-_.]+$/,
                  message: "Invalid name. Allowed: letters, numbers, spaces, - _ .",
                }, maxLength: {
                  value: 200,
                  message: `Cannot exceed 200 characters`,
                }
              }}
              className="mb-2"
            >
              <input autoComplete="off" placeholder="Name" />
            </RFG>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <XDateRange
              minDate={selectedCam?.first_image_time}
              maxDate={selectedCam?.latest_image_time}
              onChange={(start, end) => {
                setValue("date_from", start);
                setValue("date_to", end);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <RFG
              name="video_duration"
              label="Video Duration (in Minutes)"
              control={control}
              rules={{
                required: true,
                pattern: {
                  value: /^[0-9]{1,3}$/,
                  message: "Must be a valid number of minutes (e.g. 1 to 180)",
                },
                maxLength: {
                  value: 3,
                  message: `Cannot exceed 3 digits (max 180 minutes)`
                }
              }}
              className="mb-2"
            >
              <input type="number" min="1" max="180" autoComplete="off" placeholder="Video Duration in minutes (e.g. 1)" />
            </RFG>

            <Box className="mb-3" sx={{ mt: 1 }}>
              <label className="input-label" style={{ display: 'block', marginBottom: 4 }}>
                Day Only (Exclude Night Images 17:00-08:00)
              </label>
              <Box display="flex" alignItems="center" mt={1}>
                <input
                  type="checkbox"
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                  checked={Boolean(watch("day_only"))}
                  onChange={(e) => {
                    setValue("day_only", e.target.checked, { shouldValidate: true, shouldDirty: true });
                  }}
                />
                <Typography
                  sx={{ ml: 1, fontSize: 13, color: '#555', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => {
                    setValue("day_only", !watch("day_only"), { shouldValidate: true, shouldDirty: true });
                  }}
                >
                  Check to process only daytime images
                </Typography>
              </Box>
            </Box>

            <Box textAlign="right" sx={{ my: 4 }}>
              <Button className="btn-primary" sx={{ mx: 2 }} onClick={back}>
                Back
              </Button>
              <Button sx={{ mx: 2 }} className="btn-primary" type="submit">Save</Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </form>
  );
}
